/**
 * Axios API Instance
 * Centralized HTTP client with interceptors for auth, logging, and error handling
 */

import axios from 'axios';
import config from '../config/env';

// Create axios instance
const api = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: config.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor
 * Adds authentication token to every request
 */
api.interceptors.request.use(
  (requestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('tcr_customer_token');

    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (config.isDevelopment) {
      console.log('🚀 API Request:', {
        method: requestConfig.method?.toUpperCase(),
        url: requestConfig.url,
        data: requestConfig.data,
      });
    }

    return requestConfig;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles common response patterns and errors
 */
api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (config.isDevelopment) {
      console.log('✅ API Response:', {
        url: response.config.url,
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response;

      // Token expired or invalid - redirect to login
      if (status === 401) {
        console.warn('🔒 Authentication failed - clearing session');
        localStorage.removeItem('tcr_customer_token');
        localStorage.removeItem('tcr_customer_data');

        // Only redirect if not already on auth page
        if (!window.location.pathname.includes('/auth')) {
          window.dispatchEvent(new Event('auth_unauthorized'));
        }
      }

      // Forbidden
      if (status === 403) {
        console.warn('🚫 Access forbidden:', data.message);
      }

      // Server error
      if (status >= 500) {
        console.error('💥 Server error:', data.message);
      }

      // Log error in development
      if (config.isDevelopment) {
        console.error('❌ API Error:', {
          status,
          message: data.message || error.message,
          url: error.config?.url,
        });
      }
    } else if (error.request) {
      // Request made but no response
      console.error('📡 Network error - no response received');
    } else {
      // Something else happened
      console.error('❌ Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
